import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Check, AlertTriangle, X, Clock, Film, StickyNote, Image, Camera } from 'lucide-react';
import toast from 'react-hot-toast';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { Cliente, Contenuto, StatoContenuto } from '../types';
import StatsCard from '../components/ui/StatsCard';
import PageHeader from '../components/ui/PageHeader';
import { isPost, tipoDi, isCaroselloFoto, etichettaDi, etichettaPostCliente } from '../utils/tipoContenuto';
import { giorniUscita, alternaReelEPost, tipoAttesoInData } from '../utils/pianoUscite';

const API_BASE_URL = '/api';

const GIORNI_DB = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì'];
const GIORNI_SHORT = ['LUN', 'MAR', 'MER', 'GIO', 'VEN'];
const GIORNI_JS = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];

// ===== Helper date (settimana Lun-Ven, senza fusi orari di mezzo) =====
const ymd = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const addGiorni = (d: Date, n: number): Date => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

// Lunedì della settimana che contiene `d`
const inizioSettimana = (d: Date): Date => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};

const MS_SETTIMANA = 7 * 24 * 60 * 60 * 1000;

// Colori sobri per canale, così il video editor riconosce a colpo d'occhio dove pubblicare
const CHANNEL_BADGE: Record<string, string> = {
  Instagram: 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',
  TikTok: 'bg-slate-200 text-slate-800 dark:bg-gray-600 dark:text-slate-100',
  YouTube: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  Facebook: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  LinkedIn: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
};

// Reel o post in quel giorno (solo per chi alterna): un colpo d'occhio sulla
// scacchiera del feed. Il post può essere un post grafico o un carosello foto
// (03/11/2026): il giorno da post è lo stesso, il formato lo dice il contenuto.
// Il nome del post: quello del contenuto già in calendario, se no quello della scheda del cliente
// («Carosello foto» per chi fa solo caroselli foto, «Post grafico» per chi fa solo grafici, «Post» se tutti e due).
const TipoBadge: React.FC<{ tipo: 'reel' | 'post' | null; cliente?: Cliente; contenuto?: Contenuto }> = ({ tipo, cliente, contenuto }) => {
  if (!tipo) return null;
  const nome = contenuto && isPost(contenuto) ? etichettaDi(contenuto) : etichettaPostCliente(cliente);
  return tipo === 'post' ? (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none whitespace-nowrap bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" title={`Oggi tocca a un ${nome.toLowerCase()}`}>
      {nome === 'Carosello foto' ? <Camera className="w-3 h-3" /> : <Image className="w-3 h-3" />} {nome}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none whitespace-nowrap bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" title="Oggi tocca a un reel">
      <Film className="w-3 h-3" /> Reel
    </span>
  );
};

const ChannelBadges: React.FC<{ piattaforme: string[]; center?: boolean }> = ({ piattaforme, center }) => {
  if (!piattaforme || piattaforme.length === 0) {
    return <span className="text-[10px] text-slate-300 dark:text-slate-600">Nessun canale</span>;
  }
  return (
    <div className={`flex flex-wrap items-center gap-1 ${center ? 'justify-center' : ''}`}>
      {piattaforme.map((p) => (
        <span
          key={p}
          className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none whitespace-nowrap ${
            CHANNEL_BADGE[p] || 'bg-slate-100 text-slate-600 dark:bg-gray-700 dark:text-slate-300'
          }`}
        >
          {p}
        </span>
      ))}
    </div>
  );
};

const CalendarioEditoriale: React.FC = () => {
  const { state } = useData();
  const { getToken } = useAuth();
  // getToken viene ricreato a ogni render del context: lo tengo in un ref così
  // non fa ripartire in loop il caricamento della settimana.
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  const [isMobile, setIsMobile] = useState(false);

  // ===== Settimana visualizzata =====
  // Il calendario non è più "la settimana in corso e basta": si scorre avanti
  // (e indietro) e ogni spunta resta attaccata alla data esatta di uscita.
  const [inizioSettimanaVista, setInizioSettimanaVista] = useState<Date>(() => inizioSettimana(new Date()));

  // Su mobile si vede un giorno alla volta: parti da OGGI (Lun-Ven), non sempre da Lunedì.
  // Nel weekend (oggi non è un giorno lavorativo) ripiega su Lunedì.
  const [currentDayIndex, setCurrentDayIndex] = useState(() => {
    const idxOggi = GIORNI_DB.indexOf(GIORNI_JS[new Date().getDay()]);
    return idxOggi >= 0 ? idxOggi : 0;
  });

  // Popup di conferma prima di spuntare la pubblicazione di un cliente in un dato giorno
  const [confirm, setConfirm] = useState<{ cliente: Cliente; giorno: string; data: string } | null>(null);
  // Canali che il video editor ha confermato di aver verificato dentro al popup
  const [canaliVerificati, setCanaliVerificati] = useState<string[]>([]);
  // Nota libera su quel cliente/giorno (es. "contenuto slittato a giovedì")
  const [notaTesto, setNotaTesto] = useState('');

  // Spunte salvate: chiave `${clienteId}|${YYYY-MM-DD}` -> canali confermati
  const [pubblicazioni, setPubblicazioni] = useState<Record<string, string[]>>({});
  // Note salvate, stessa chiave delle spunte
  const [note, setNote] = useState<Record<string, string>>({});
  // Giorni segnati in pubblica «non esce per il cliente» (29/10/2026), stessa chiave: il motivo ('' se non scritto)
  const [nonEsce, setNonEsce] = useState<Record<string, string>>({});
  const [caricamentoSettimana, setCaricamentoSettimana] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Giorni (con data reale) della settimana visualizzata
  const giorniSettimana = useMemo(
    () =>
      GIORNI_DB.map((nome, i) => {
        const data = addGiorni(inizioSettimanaVista, i);
        return { nome, short: GIORNI_SHORT[i], data, ymd: ymd(data) };
      }),
    [inizioSettimanaVista]
  );

  const oggi = new Date();
  const oggiYmd = ymd(oggi);
  const oggiNome = GIORNI_JS[oggi.getDay()];
  // Indice del giorno corrente dentro la settimana visualizzata (-1 se non c'è)
  const oggiIndex = giorniSettimana.findIndex((g) => g.ymd === oggiYmd);

  const offsetSettimane = Math.round(
    (inizioSettimanaVista.getTime() - inizioSettimana(oggi).getTime()) / MS_SETTIMANA
  );

  const etichettaSettimana = useMemo(() => {
    const fine = addGiorni(inizioSettimanaVista, 4);
    const inizioTxt = inizioSettimanaVista.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
    const fineTxt = fine.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${inizioTxt} – ${fineTxt}`;
  }, [inizioSettimanaVista]);

  const etichettaOffset = useMemo(() => {
    if (offsetSettimane === 0) return 'Questa settimana';
    if (offsetSettimane === 1) return 'Prossima settimana';
    if (offsetSettimane === -1) return 'Settimana scorsa';
    return offsetSettimane > 0 ? `Tra ${offsetSettimane} settimane` : `${Math.abs(offsetSettimane)} settimane fa`;
  }, [offsetSettimane]);

  // Carica le spunte della settimana visualizzata
  useEffect(() => {
    let annullato = false;
    const from = ymd(inizioSettimanaVista);
    const to = ymd(addGiorni(inizioSettimanaVista, 4));

    const carica = async () => {
      setCaricamentoSettimana(true);
      try {
        const response = await fetch(`${API_BASE_URL}/clients/publication-days?from=${from}&to=${to}`, {
          headers: { Authorization: `Bearer ${getTokenRef.current()}` },
        });
        if (!response.ok) throw new Error('Failed to load publication days');
        const json = await response.json();
        if (annullato) return;
        const mappa: Record<string, string[]> = {};
        const mappaNote: Record<string, string> = {};
        const mappaNonEsce: Record<string, string> = {};
        (json.data || []).forEach((riga: any) => {
          const chiave = `${riga.client_id}|${riga.publication_date}`;
          mappa[chiave] = Array.isArray(riga.channels) ? riga.channels : [];
          if (riga.note) mappaNote[chiave] = riga.note;
          if (riga.non_esce) mappaNonEsce[chiave] = riga.non_esce_motivo || '';
        });
        setPubblicazioni(mappa);
        setNote(mappaNote);
        setNonEsce(mappaNonEsce);
      } catch (error) {
        console.error('Error loading publication days:', error);
        if (!annullato) {
          setPubblicazioni({});
          setNote({});
          setNonEsce({});
          toast.error('Errore caricamento pubblicazioni della settimana');
        }
      } finally {
        if (!annullato) setCaricamentoSettimana(false);
      }
    };

    carica();
    return () => {
      annullato = true;
    };
  }, [inizioSettimanaVista]);

  const clientiAttivi = useMemo(() => {
    return state.clienti.filter(c => c.status === 'active');
  }, [state.clienti]);

  // Giorni di uscita di ogni cliente: quelli della scheda, più quelli aggiunti
  // in automatico per chi alterna reel e post grafici (vedi giorniUscita).
  const clientiPerGiorno = useMemo(() => {
    const result: Record<string, Cliente[]> = {};
    GIORNI_DB.forEach((giorno) => {
      result[giorno] = clientiAttivi.filter(cliente =>
        giorniUscita(cliente).includes(giorno)
      );
    });
    return result;
  }, [clientiAttivi]);

  // Etichetta Reel/Post per cliente e data: il tipo del contenuto già in
  // calendario, altrimenti quello che tocca secondo l'alternanza a scacchiera.
  // Solo per i clienti che prevedono i post grafici.
  const tipoPerData = useCallback(
    (cliente: Cliente, dataYmd: string, contenuto?: Contenuto): 'reel' | 'post' | null => {
      if (!alternaReelEPost(cliente)) return null;
      if (contenuto) return tipoDi(contenuto);
      return tipoAttesoInData(cliente, new Date(`${dataYmd}T12:00:00`));
    },
    []
  );

  // Contenuto già programmato per cliente e data: è la "pubblicazione già
  // organizzata" che si vede scorrendo avanti nelle settimane.
  const contenutiPerData = useMemo(() => {
    const mappa: Record<string, Contenuto> = {};
    state.contenuti.forEach((c) => {
      if (!c.dataPubblicazione) return;
      if (
        c.stato !== StatoContenuto.PROGRAMMATO &&
        c.stato !== StatoContenuto.PUBBLICATO &&
        c.stato !== StatoContenuto.ARCHIVIATO
      ) {
        return;
      }
      mappa[`${c.clienteId}|${ymd(new Date(c.dataPubblicazione))}`] = c;
    });
    return mappa;
  }, [state.contenuti]);

  // ===== Navigazione =====
  const cambiaSettimana = (direzione: number) => {
    setInizioSettimanaVista((prev) => addGiorni(prev, direzione * 7));
  };

  const vaiAOggi = () => {
    setInizioSettimanaVista(inizioSettimana(new Date()));
    const idxOggi = GIORNI_DB.indexOf(GIORNI_JS[new Date().getDay()]);
    setCurrentDayIndex(idxOggi >= 0 ? idxOggi : 0);
  };

  // Su mobile, oltre il venerdì si passa alla settimana dopo (e viceversa):
  // così si scorre avanti senza dover tornare al selettore di settimana.
  const navigateDay = (direction: number) => {
    const newIndex = currentDayIndex + direction;
    if (newIndex < 0) {
      setInizioSettimanaVista((prev) => addGiorni(prev, -7));
      setCurrentDayIndex(GIORNI_DB.length - 1);
      return;
    }
    if (newIndex >= GIORNI_DB.length) {
      setInizioSettimanaVista((prev) => addGiorni(prev, 7));
      setCurrentDayIndex(0);
      return;
    }
    setCurrentDayIndex(newIndex);
  };

  // ===== Uscite di oggi: cosa pubblicare per i clienti che escono oggi =====
  // Resta sempre agganciata a OGGI, anche mentre guardi una settimana futura.
  const contenutiDaPubblicareOggi = (clientiPerGiorno[oggiNome] || []).length;
  const usciteOggi = useMemo(() => {
    const clientiOggi = clientiPerGiorno[oggiNome] || [];
    return clientiOggi.map((cliente) => {
      // Contenuto già programmato/pubblicato oggi per questo cliente
      const programmato = state.contenuti.find((c) => {
        if (c.clienteId !== cliente.id || !c.dataPubblicazione) return false;
        return ymd(new Date(c.dataPubblicazione)) === oggiYmd && (
          c.stato === StatoContenuto.PROGRAMMATO ||
          c.stato === StatoContenuto.PUBBLICATO ||
          c.stato === StatoContenuto.ARCHIVIATO
        );
      });
      if (programmato) return { cliente, tipo: 'programmato' as const, contenuto: programmato };
      // Altrimenti suggerisci: se oggi tocca a un post (grafico o carosello foto), i post col testo
      // confermato; se no i reel "registrati" (girati, non ancora programmati).
      const toccaPost = tipoPerData(cliente, oggiYmd) === 'post';
      const suggeriti = state.contenuti.filter((c) =>
        c.clienteId === cliente.id &&
        !c.campagnaAdv &&
        (toccaPost
          ? isPost(c) && c.stato !== StatoContenuto.BOZZA
          : !isPost(c) && c.videoRegistrato) &&
        c.stato !== StatoContenuto.PROGRAMMATO &&
        c.stato !== StatoContenuto.PUBBLICATO &&
        c.stato !== StatoContenuto.ARCHIVIATO
      );
      return { cliente, tipo: 'suggerito' as const, suggeriti, toccaPost };
    });
  }, [clientiPerGiorno, oggiNome, oggiYmd, state.contenuti, tipoPerData]);

  // Canali confermati per un cliente in una data precisa
  const canaliConfermati = useCallback(
    (cliente: Cliente, data: string): string[] => pubblicazioni[`${cliente.id}|${data}`] || [],
    [pubblicazioni]
  );

  const notaDi = useCallback(
    (cliente: Cliente, data: string): string => note[`${cliente.id}|${data}`] || '',
    [note]
  );

  // Stato di pubblicazione di un cliente in una data, in base ai canali confermati:
  //  'completo'  → tutti i canali del cliente (pallino verde)
  //  'parziale'  → alcuni ma non tutti (pallino arancione: manca qualcosa)
  //  'vuoto'     → nessun canale confermato (pallino neutro)
  const statoPubblicazione = (cliente: Cliente, data: string): 'completo' | 'parziale' | 'vuoto' => {
    const confermati = canaliConfermati(cliente, data);
    if (confermati.length === 0) return 'vuoto';
    const richiesti = cliente.piattaforme || [];
    const tutti = richiesti.length > 0 && richiesti.every((c) => confermati.includes(c));
    return tutti ? 'completo' : 'parziale';
  };

  // Testo del tooltip del pallino
  const titoloPallino = (cliente: Cliente, data: string, stato: string): string => {
    const confermati = canaliConfermati(cliente, data);
    const totali = cliente.piattaforme?.length || 0;
    if (stato === 'completo') return 'Pubblicato su tutti i canali';
    if (stato === 'parziale') {
      const mancanti = (cliente.piattaforme || []).filter((c) => !confermati.includes(c));
      return `Pubblicato su ${confermati.length}/${totali} canali — manca: ${mancanti.join(', ')}`;
    }
    return 'Segna pubblicazione (spunta i canali)';
  };

  // Il click sul pallino apre SEMPRE il popup, pre-compilato con i canali già
  // confermati per quella data: così puoi aggiungere quello mancante in un secondo momento.
  const apriPopup = (e: React.MouseEvent, cliente: Cliente, giorno: string, data: string) => {
    e.stopPropagation();
    setCanaliVerificati(canaliConfermati(cliente, data));
    setNotaTesto(notaDi(cliente, data));
    setConfirm({ cliente, giorno, data });
  };

  const toggleCanaleVerificato = (canale: string) => {
    setCanaliVerificati((prev) =>
      prev.includes(canale) ? prev.filter((c) => c !== canale) : [...prev, canale]
    );
  };

  const chiudiConferma = () => {
    setConfirm(null);
    setCanaliVerificati([]);
    setNotaTesto('');
  };

  // Salva canali spuntati + nota per QUELLA data (parziale ammesso; nessun
  // canale e nessuna nota = giorno azzerato). Update ottimistico + rollback.
  const confermaPubblicazione = async () => {
    if (!confirm) return;
    const { cliente, giorno, data } = confirm;
    const chiave = `${cliente.id}|${data}`;
    const precedenti = pubblicazioni[chiave];
    const notaPrecedente = note[chiave];
    const canali = [...canaliVerificati];
    const nota = notaTesto.trim();
    const richiesti = cliente.piattaforme || [];
    const full = richiesti.length > 0 && richiesti.every((c) => canali.includes(c));

    setPubblicazioni((prev) => {
      const next = { ...prev };
      if (canali.length === 0) delete next[chiave];
      else next[chiave] = canali;
      return next;
    });
    setNote((prev) => {
      const next = { ...prev };
      if (nota === '') delete next[chiave];
      else next[chiave] = nota;
      return next;
    });
    chiudiConferma();

    try {
      const response = await fetch(`${API_BASE_URL}/clients/${cliente.id}/weekly-published`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getTokenRef.current()}`,
        },
        body: JSON.stringify({ day: giorno, date: data, channels: canali, full, note: nota }),
      });
      if (!response.ok) throw new Error('Failed to update publication day');
    } catch (error) {
      console.error('Error updating publication day:', error);
      setPubblicazioni((prev) => {
        const next = { ...prev };
        if (precedenti) next[chiave] = precedenti;
        else delete next[chiave];
        return next;
      });
      setNote((prev) => {
        const next = { ...prev };
        if (notaPrecedente) next[chiave] = notaPrecedente;
        else delete next[chiave];
        return next;
      });
      toast.error('Errore aggiornamento pubblicazione');
    }
  };

  const giornoCorrenteMobile = giorniSettimana[currentDayIndex];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-gray-900 -mx-3 -mb-3 sm:-mx-4 sm:-mb-4 md:-mx-5 md:-mb-5 lg:-mx-6 lg:-mb-6 xl:-mx-8 xl:-mb-8">
      {/* Header */}
      <div className="px-4 sm:px-6 pt-4 sm:pt-6">
        <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-4">
          <PageHeader
            title="Calendario"
            description="Calendario editoriale delle uscite dei clienti"
          />
          <StatsCard
            value={contenutiDaPubblicareOggi}
            label="contenuti da pubblicare oggi"
            variant="success"
          />
        </div>

        {/* Selettore settimana: si scorre avanti/indietro e le spunte restano
            salvate sulla data, non si azzerano più ogni domenica. */}
        <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl bg-white dark:bg-gray-800 shadow-sm px-3 py-2.5">
          <button
            type="button"
            onClick={() => cambiaSettimana(-1)}
            title="Settimana precedente"
            className="w-9 h-9 rounded-full bg-slate-100 dark:bg-gray-700 flex items-center justify-center hover:bg-slate-200 dark:hover:bg-gray-600 transition-colors flex-shrink-0"
          >
            <ChevronLeft className="w-5 h-5 text-slate-600 dark:text-slate-300" />
          </button>

          <div className="min-w-0 text-center">
            <p className="text-sm sm:text-base font-bold text-slate-900 dark:text-white truncate">
              {etichettaSettimana}
            </p>
            <p
              className={`text-[11px] sm:text-xs font-semibold truncate ${
                offsetSettimane === 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400'
              }`}
            >
              {etichettaOffset}
              {caricamentoSettimana ? ' · aggiorno…' : ''}
            </p>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {offsetSettimane !== 0 && (
              <button
                type="button"
                onClick={vaiAOggi}
                className="px-3 h-9 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-xs font-semibold hover:brightness-110 transition-all"
              >
                Oggi
              </button>
            )}
            <button
              type="button"
              onClick={() => cambiaSettimana(1)}
              title="Settimana successiva"
              className="w-9 h-9 rounded-full bg-slate-100 dark:bg-gray-700 flex items-center justify-center hover:bg-slate-200 dark:hover:bg-gray-600 transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-slate-600 dark:text-slate-300" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile View */}
      {isMobile ? (
        <div className="p-4">
          {/* Day Selector */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigateDay(-1)}
              className="w-10 h-10 rounded-full bg-white dark:bg-gray-800 shadow-sm flex items-center justify-center"
            >
              <ChevronLeft className="w-5 h-5 text-slate-600 dark:text-slate-300" />
            </button>

            <div className="flex gap-2">
              {giorniSettimana.map((g, i) => (
                <button
                  key={g.ymd}
                  onClick={() => setCurrentDayIndex(i)}
                  className={`w-10 h-10 rounded-full text-[11px] font-bold leading-tight flex flex-col items-center justify-center transition-all ${
                    i === currentDayIndex
                      ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 scale-110'
                      : i === oggiIndex
                      ? 'bg-emerald-500 text-white'
                      : 'bg-white dark:bg-gray-800 text-slate-500 dark:text-slate-400'
                  }`}
                >
                  <span>{g.short.charAt(0)}</span>
                  <span className="text-[10px] font-semibold opacity-80">{g.data.getDate()}</span>
                </button>
              ))}
            </div>

            <button
              onClick={() => navigateDay(1)}
              className="w-10 h-10 rounded-full bg-white dark:bg-gray-800 shadow-sm flex items-center justify-center"
            >
              <ChevronRight className="w-5 h-5 text-slate-600 dark:text-slate-300" />
            </button>
          </div>

          {/* Day Title — sfondo verde se è il giorno corrente */}
          <h2 className="text-center mb-6">
            <span
              className={`inline-block text-2xl font-bold ${
                giornoCorrenteMobile.ymd === oggiYmd
                  ? 'bg-emerald-500 text-white rounded-xl px-4 py-1'
                  : 'text-slate-900 dark:text-white'
              }`}
            >
              {giornoCorrenteMobile.nome}
            </span>
            <span className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mt-1">
              {giornoCorrenteMobile.data.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
          </h2>

          {/* Client List (una riga per cliente, canali a piena larghezza) */}
          <div className="space-y-3">
            {clientiPerGiorno[giornoCorrenteMobile.nome]?.length === 0 ? (
              <div className="py-12 text-center text-slate-400">
                Nessun cliente
              </div>
            ) : (
              clientiPerGiorno[giornoCorrenteMobile.nome]?.map((cliente) => {
                const dataGiorno = giornoCorrenteMobile.ymd;
                const stato = statoPubblicazione(cliente, dataGiorno);
                const contenuto = contenutiPerData[`${cliente.id}|${dataGiorno}`];
                const nota = notaDi(cliente, dataGiorno);
                const pClass =
                  stato === 'completo'
                    ? 'bg-emerald-500 border-emerald-500 ring-emerald-500 text-black'
                    : stato === 'parziale'
                    ? 'bg-amber-500 border-amber-500 ring-amber-500 text-black'
                    : 'bg-slate-100 dark:bg-gray-700 border-white ring-slate-200 dark:ring-gray-600 text-transparent';
                return (
                  <div
                    key={cliente.id}
                    onClick={(e) => apriPopup(e, cliente, giornoCorrenteMobile.nome, dataGiorno)}
                    title="Tocca per canali e nota"
                    className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-2xl p-3 shadow-sm cursor-pointer active:bg-slate-50 dark:active:bg-gray-700 transition-colors"
                  >
                    <button
                      type="button"
                      onClick={(e) => apriPopup(e, cliente, giornoCorrenteMobile.nome, dataGiorno)}
                      title={titoloPallino(cliente, dataGiorno, stato)}
                      aria-pressed={stato === 'completo'}
                      className={`w-7 h-7 rounded-full border-2 ring-1 flex items-center justify-center flex-shrink-0 transition-colors ${pClass}`}
                    >
                      <Check className="w-4 h-4" strokeWidth={3} />
                    </button>
                    <div className="w-11 h-11 rounded-full overflow-hidden bg-slate-100 dark:bg-gray-600 flex-shrink-0 shadow-sm">
                      {cliente.logoUrl ? (
                        <img src={cliente.logoUrl} alt={cliente.nomeAzienda} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-base font-bold text-slate-400 dark:text-slate-500">
                          {cliente.nomeAzienda?.charAt(0)?.toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-slate-900 dark:text-white text-sm truncate">
                          {cliente.nomeAzienda}
                        </span>
                        <span className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 dark:text-slate-400 whitespace-nowrap">
                          <Clock className="w-3 h-3" />
                          {cliente.orarioPubblicazione || '21:00'}
                        </span>
                      </div>
                      {contenuto && (
                        <p
                          className="mt-1 flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400 truncate"
                          title={`${etichettaDi(contenuto)}: ${contenuto.titolo}`}
                        >
                          {isCaroselloFoto(contenuto) ? <Camera className="w-3 h-3 flex-shrink-0" /> : isPost(contenuto) ? <Image className="w-3 h-3 flex-shrink-0" /> : <Film className="w-3 h-3 flex-shrink-0" />}
                          <span className="truncate">{contenuto.titolo}</span>
                        </p>
                      )}
                      {`${cliente.id}|${dataGiorno}` in nonEsce && (
                        <p
                          className="mt-1.5 flex items-start gap-1 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-2 py-1 text-[11px] font-semibold text-red-700 dark:text-red-300"
                          title={nonEsce[`${cliente.id}|${dataGiorno}`] || 'Segnato in pubblica: non esce per il cliente'}
                        >
                          <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-[2px]" />
                          <span className="line-clamp-3">
                            Non esce: problema del cliente{nonEsce[`${cliente.id}|${dataGiorno}`] ? ` · ${nonEsce[`${cliente.id}|${dataGiorno}`]}` : ''}
                          </span>
                        </p>
                      )}
                      {nota && (
                        <p
                          className="mt-1.5 flex items-start gap-1 rounded-lg bg-amber-50 dark:bg-amber-900/20 px-2 py-1 text-[11px] text-amber-800 dark:text-amber-300"
                          title={nota}
                        >
                          <StickyNote className="w-3 h-3 flex-shrink-0 mt-[2px]" />
                          <span className="line-clamp-2">{nota}</span>
                        </p>
                      )}
                      <div className="mt-1.5 flex flex-wrap items-center gap-1">
                        <TipoBadge tipo={tipoPerData(cliente, dataGiorno, contenuto)} cliente={cliente} contenuto={contenuto} />
                        <ChannelBadges piattaforme={cliente.piattaforme} />
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      ) : (
        /* Desktop View - 5 Column Grid */
        <div className="p-6">
          <div className="grid grid-cols-5 gap-4">
            {giorniSettimana.map(({ nome: giorno, data, ymd: dataGiorno }) => {
              const isOggi = dataGiorno === oggiYmd;
              return (
              <div key={dataGiorno} className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm overflow-hidden">
                {/* Day Header — evidenziato in verde solo per il giorno corrente */}
                <div
                  className={`mx-2 mt-2 mb-1 border rounded-xl px-4 py-2.5 ${
                    isOggi
                      ? 'bg-emerald-500 border-emerald-500'
                      : 'border-slate-300 dark:border-white/20'
                  }`}
                >
                  <h3
                    className={`font-bold text-center ${
                      isOggi ? 'text-white' : 'text-slate-900 dark:text-white'
                    }`}
                  >
                    {giorno}
                  </h3>
                  <p
                    className={`text-xs text-center mt-0.5 ${
                      isOggi ? 'text-white/80' : 'text-slate-500 dark:text-slate-400'
                    }`}
                  >
                    {data.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })} ·{' '}
                    {clientiPerGiorno[giorno]?.length || 0} clienti
                  </p>
                </div>

                {/* Client List */}
                <div className="p-3 space-y-3 min-h-[200px]">
                  {clientiPerGiorno[giorno]?.length === 0 ? (
                    <div className="h-full flex items-center justify-center py-8">
                      <span className="text-slate-300 dark:text-slate-600 text-sm">-</span>
                    </div>
                  ) : (
                    clientiPerGiorno[giorno]?.map((cliente) => {
                      const stato = statoPubblicazione(cliente, dataGiorno);
                      const contenuto = contenutiPerData[`${cliente.id}|${dataGiorno}`];
                      const nota = notaDi(cliente, dataGiorno);
                      const pClass =
                        stato === 'completo'
                          ? 'bg-emerald-500 border-emerald-500 ring-emerald-500 text-black'
                          : stato === 'parziale'
                          ? 'bg-amber-500 border-amber-500 ring-amber-500 text-black'
                          : 'bg-transparent border-white ring-slate-200 dark:ring-transparent text-transparent hover:bg-white/30';
                      return (
                      <div
                        key={cliente.id}
                        onClick={(e) => apriPopup(e, cliente, giorno, dataGiorno)}
                        title="Clicca per canali e nota"
                        className="p-2 rounded-xl cursor-pointer hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="flex items-center gap-2.5">
                          <button
                            type="button"
                            onClick={(e) => apriPopup(e, cliente, giorno, dataGiorno)}
                            title={titoloPallino(cliente, dataGiorno, stato)}
                            aria-pressed={stato === 'completo'}
                            className={`w-6 h-6 rounded-full border-2 shadow-sm ring-1 flex items-center justify-center flex-shrink-0 transition-colors ${pClass}`}
                          >
                            <Check className="w-3.5 h-3.5" strokeWidth={3} />
                          </button>
                          <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-100 dark:bg-gray-600 flex-shrink-0 shadow-sm">
                            {cliente.logoUrl ? (
                              <img
                                src={cliente.logoUrl}
                                alt={cliente.nomeAzienda}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-sm font-bold text-slate-400 dark:text-slate-500">
                                {cliente.nomeAzienda?.charAt(0)?.toUpperCase()}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0 font-medium text-slate-900 dark:text-white text-sm truncate">
                            {cliente.nomeAzienda}
                          </div>
                          <span className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 dark:text-slate-400 whitespace-nowrap">
                            <Clock className="w-3 h-3" />
                            {cliente.orarioPubblicazione || '21:00'}
                          </span>
                        </div>
                        {contenuto && (
                          <p
                            className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400 truncate"
                            title={`${etichettaDi(contenuto)}: ${contenuto.titolo}`}
                          >
                            {isCaroselloFoto(contenuto) ? <Camera className="w-3 h-3 flex-shrink-0" /> : isPost(contenuto) ? <Image className="w-3 h-3 flex-shrink-0" /> : <Film className="w-3 h-3 flex-shrink-0" />}
                            <span className="truncate">{contenuto.titolo}</span>
                          </p>
                        )}
                        {`${cliente.id}|${dataGiorno}` in nonEsce && (
                          <p
                            className="mt-1.5 flex items-start gap-1 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-2 py-1 text-[11px] font-semibold text-red-700 dark:text-red-300"
                            title={nonEsce[`${cliente.id}|${dataGiorno}`] || 'Segnato in pubblica: non esce per il cliente'}
                          >
                            <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-[2px]" />
                            <span className="line-clamp-3">
                              Non esce: problema del cliente{nonEsce[`${cliente.id}|${dataGiorno}`] ? ` · ${nonEsce[`${cliente.id}|${dataGiorno}`]}` : ''}
                            </span>
                          </p>
                        )}
                        {nota && (
                          <p
                            className="mt-1.5 flex items-start gap-1 rounded-lg bg-amber-50 dark:bg-amber-900/20 px-2 py-1 text-[11px] text-amber-800 dark:text-amber-300"
                            title={nota}
                          >
                            <StickyNote className="w-3 h-3 flex-shrink-0 mt-[2px]" />
                            <span className="line-clamp-2">{nota}</span>
                          </p>
                        )}
                        <div className="mt-1.5 flex flex-wrap items-center gap-1">
                          <TipoBadge tipo={tipoPerData(cliente, dataGiorno, contenuto)} cliente={cliente} contenuto={contenuto} />
                          <ChannelBadges piattaforme={cliente.piattaforme} />
                        </div>
                      </div>
                      );
                    })
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ===== Uscite di oggi ===== */}
      <div className="px-4 sm:px-6 pb-6 mt-2">
        <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">
          Uscite di oggi{GIORNI_DB.includes(oggiNome) ? ` · ${oggiNome}` : ''}
        </h2>
        <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mb-3">
          Cosa pubblicare per i clienti che escono oggi
        </p>

        {usciteOggi.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 text-center">
            <p className="text-sm text-slate-500 dark:text-slate-400">Nessun cliente pubblica oggi.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {usciteOggi.map((u) => (
              <div
                key={u.cliente.id}
                className={`rounded-2xl border p-4 ${
                  u.tipo === 'programmato'
                    ? 'border-emerald-300 dark:border-emerald-700/60 bg-emerald-50/50 dark:bg-emerald-900/10'
                    : 'border-amber-300 dark:border-amber-700/60 bg-amber-50/50 dark:bg-amber-900/10'
                }`}
              >
                {/* Cliente */}
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-9 h-9 rounded-full overflow-hidden bg-slate-100 dark:bg-gray-700 flex-shrink-0 border border-slate-200 dark:border-gray-600">
                    {u.cliente.logoUrl ? (
                      <img src={u.cliente.logoUrl} alt={u.cliente.nomeAzienda} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-sm font-semibold text-slate-500 dark:text-slate-400">
                        {u.cliente.nomeAzienda?.charAt(0)?.toUpperCase()}
                      </div>
                    )}
                  </div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{u.cliente.nomeAzienda}</p>
                </div>

                {u.tipo === 'programmato' ? (
                  <>
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-2 leading-snug flex items-start gap-1.5">
                      {isCaroselloFoto(u.contenuto) ? <Camera className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-purple-600" /> : isPost(u.contenuto) && <Image className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-purple-600" />}
                      <span>{u.contenuto.titolo}</span>
                    </p>
                    <div className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                      <Check className="w-3.5 h-3.5 flex-shrink-0" /> Contenuto come da programma
                    </div>
                  </>
                ) : (
                  <>
                    <div className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 dark:text-amber-400 mb-2">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> Non c'è in calendario, ti suggerisco questi{u.toccaPost ? ` ${etichettaPostCliente(u.cliente) === 'Carosello foto' ? 'caroselli foto' : etichettaPostCliente(u.cliente) === 'Post grafico' ? 'post grafici' : 'post'}` : ''}
                    </div>
                    {u.suggeriti.length > 0 ? (
                      <ul className="space-y-1">
                        {u.suggeriti.slice(0, 4).map((c) => (
                          <li key={c.id} className="text-sm text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                            <span className="truncate">{c.titolo}</span>
                            {u.toccaPost && <span className="flex-shrink-0 text-[10px] font-semibold text-purple-600 dark:text-purple-400">{etichettaDi(c)}</span>}
                          </li>
                        ))}
                        {u.suggeriti.length > 4 && (
                          <li className="text-xs text-slate-400 dark:text-slate-500 pl-3">+{u.suggeriti.length - 4} altri</li>
                        )}
                      </ul>
                    ) : (
                      <p className="text-xs text-slate-500 dark:text-slate-400 italic">{u.toccaPost ? `Nessun ${etichettaPostCliente(u.cliente).toLowerCase()} confermato disponibile.` : 'Nessun contenuto registrato disponibile.'}</p>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Popup di conferma pubblicazione — dinamico sui canali del cliente */}
      {confirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={chiudiConferma}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-2xl overflow-y-auto max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 p-5 border-b border-slate-100 dark:border-gray-700">
              <div className="w-10 h-10 rounded-full bg-[#fdf751]/20 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-[#b8a800]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-slate-900 dark:text-white text-base">Pubblicazione e nota</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                  {confirm.cliente.nomeAzienda} · {confirm.giorno}{' '}
                  {new Date(`${confirm.data}T00:00:00`).toLocaleDateString('it-IT', { day: 'numeric', month: 'long' })}
                </p>
              </div>
              <button
                type="button"
                onClick={chiudiConferma}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5">
              <p className="text-sm text-slate-700 dark:text-slate-300">
                Spunta i canali su cui hai <span className="font-semibold">già pubblicato</span> il contenuto di{' '}
                <span className="font-semibold">{confirm.cliente.nomeAzienda}</span>. Tutti spuntati = pallino{' '}
                <span className="font-semibold text-emerald-600">verde</span>; solo alcuni ={' '}
                <span className="font-semibold text-amber-600">arancione</span> (manca qualcosa).
              </p>
              {confirm.cliente.piattaforme && confirm.cliente.piattaforme.length > 0 ? (
                <div className="mt-3 space-y-2">
                  {confirm.cliente.piattaforme.map((p) => {
                    const verificato = canaliVerificati.includes(p);
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => toggleCanaleVerificato(p)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 transition-colors text-left ${
                          verificato
                            ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                            : 'border-slate-200 dark:border-gray-600 hover:border-slate-300 dark:hover:border-gray-500'
                        }`}
                      >
                        <span
                          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            verificato ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 dark:border-gray-500 text-transparent'
                          }`}
                        >
                          <Check className="w-3.5 h-3.5" strokeWidth={3} />
                        </span>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                            CHANNEL_BADGE[p] || 'bg-slate-100 text-slate-600 dark:bg-gray-700 dark:text-slate-300'
                          }`}
                        >
                          {p}
                        </span>
                        <span className="text-xs text-slate-500 dark:text-slate-400 ml-auto">
                          {verificato ? 'Pubblicato ✓' : 'Da confermare'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="mt-3 text-xs text-slate-400">Questo cliente non ha canali configurati.</p>
              )}

              {/* Nota libera: serve quando un contenuto slitta o c'è qualcosa
                  da ricordarsi su quell'uscita. Resta legata a questa data. */}
              <div className="mt-4">
                <label
                  htmlFor="nota-pubblicazione"
                  className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5"
                >
                  <StickyNote className="w-3.5 h-3.5" />
                  Nota (facoltativa)
                </label>
                <textarea
                  id="nota-pubblicazione"
                  value={notaTesto}
                  onChange={(e) => setNotaTesto(e.target.value)}
                  rows={3}
                  maxLength={1000}
                  placeholder="Es. contenuto slittato a giovedì, manca il montaggio…"
                  className="w-full rounded-xl border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 resize-none"
                />
                <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
                  Resta salvata su questo giorno, anche nelle settimane future. Svuota il campo per cancellarla.
                </p>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-3 p-5 pt-0">
              <button
                type="button"
                onClick={chiudiConferma}
                className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-gray-600 text-slate-700 dark:text-slate-200 font-medium text-sm hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
              >
                Annulla
              </button>
              {(() => {
                const canali = confirm.cliente.piattaforme || [];
                const n = canaliVerificati.length;
                const tutti = canali.length > 0 && canali.every((c) => canaliVerificati.includes(c));
                const conNota = notaTesto.trim() !== '';
                // Colore e testo del pulsante rispecchiano lo stato risultante:
                // verde = completo, arancione = parziale, neutro = solo nota/azzera.
                const btnClass = tutti
                  ? 'bg-emerald-500 text-white hover:brightness-95'
                  : n > 0
                  ? 'bg-amber-500 text-white hover:brightness-95'
                  : 'bg-slate-200 dark:bg-gray-700 text-slate-600 dark:text-slate-300 hover:brightness-95';
                const label = tutti
                  ? 'Conferma — completo'
                  : n > 0
                  ? `Conferma parziale (${n}/${canali.length})`
                  : conNota
                  ? 'Salva nota'
                  : 'Azzera pubblicazione';
                return (
                  <button
                    type="button"
                    onClick={confermaPubblicazione}
                    className={`flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all ${btnClass}`}
                  >
                    {label}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarioEditoriale;
